import {
  BUSINESS_TIMEZONE,
  formatDateTime,
  formatIdr,
  formatUsd,
  type AdminTopup,
  type TopupStatus,
} from '@ytclip/shared'
import { Receipt, Search } from 'lucide-react'
import { useState } from 'react'
import { Button } from '@/components/ui/button.tsx'
import { Card, CardBody } from '@/components/ui/card.tsx'
import { DateField, SelectField, TextField } from '@/components/ui/field.tsx'
import { Badge, type BadgeTone, EmptyState, ErrorState, LoadingBlock } from '@/components/ui/feedback.tsx'
import { PageHeader, Pagination, TableScroller, Td, Th } from '@/components/ui/misc.tsx'
import { useAdminTopups } from '@/features/shared-hooks.ts'
import { useDebounced } from '@/lib/use-debounced.ts'

const STATUS_META: Record<TopupStatus, { label: string; tone: BadgeTone }> = {
  pending: { label: 'Menunggu', tone: 'warning' },
  paid: { label: 'Lunas', tone: 'success' },
  expired: { label: 'Kedaluwarsa', tone: 'neutral' },
  failed: { label: 'Gagal', tone: 'error' },
  cancelled: { label: 'Dibatalkan', tone: 'neutral' },
}

const STATUS_ORDER: TopupStatus[] = ['pending', 'paid', 'expired', 'failed', 'cancelled']

type StatusFilter = TopupStatus | 'all'

export function AdminTransactionsPage() {
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState<StatusFilter>('all')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [page, setPage] = useState(1)

  // Typing is not a filter change until it settles, otherwise every keystroke
  // becomes its own query against a table that only grows.
  const debouncedSearch = useDebounced(search, 300)

  const query = {
    page,
    pageSize: 20,
    ...(debouncedSearch ? { search: debouncedSearch } : {}),
    ...(status === 'all' ? {} : { status }),
    ...(from ? { from } : {}),
    ...(to ? { to } : {}),
  }

  const { data, isPending, isError } = useAdminTopups(query)
  const filtered = Boolean(debouncedSearch || status !== 'all' || from || to)

  /** Every filter change invalidates the current page number along with it. */
  function change<T>(apply: (value: T) => void): (value: T) => void {
    return (value) => {
      apply(value)
      setPage(1)
    }
  }

  return (
    <>
      <PageHeader title="Transaksi" description="Semua top-up dari seluruh pengguna" />

      <Card>
        <CardBody className="space-y-4">
          <div className="grid gap-3 md:grid-cols-2">
            <TextField
              label="Cari"
              placeholder="Invoice, referensi, nama, atau email"
              value={search}
              onChange={(event) => change(setSearch)(event.target.value)}
              prefix="⌕"
            />
            <SelectField
              label="Status"
              value={status}
              onChange={(event) => change(setStatus)(event.target.value as StatusFilter)}
            >
              <option value="all">Semua status</option>
              {STATUS_ORDER.map((value) => (
                <option key={value} value={value}>
                  {STATUS_META[value].label}
                </option>
              ))}
            </SelectField>
          </div>

          <div className="flex flex-wrap items-end gap-3">
            <DateField label="Dari" value={from} max={to || undefined} onChange={change(setFrom)} />
            <DateField label="Sampai" value={to} min={from || undefined} onChange={change(setTo)} />
            {filtered ? (
              <Button
                variant="text"
                onClick={() => {
                  setSearch('')
                  setStatus('all')
                  setFrom('')
                  setTo('')
                  setPage(1)
                }}
              >
                Reset filter
              </Button>
            ) : null}
          </div>

          <p className="text-label-sm text-on-surface-variant">
            Rentang tanggal memakai tanggal pembuatan invoice, waktu Jakarta ({BUSINESS_TIMEZONE}).
          </p>
        </CardBody>
      </Card>

      <Card>
        <CardBody>
          {isPending ? (
            <LoadingBlock />
          ) : isError ? (
            <ErrorState message="Daftar transaksi tidak bisa dimuat." />
          ) : data.data.length === 0 ? (
            <EmptyState
              icon={filtered ? <Search /> : <Receipt />}
              title={filtered ? 'Tidak ada yang cocok' : 'Belum ada transaksi'}
              description={filtered ? 'Coba ubah atau hapus filternya.' : undefined}
            />
          ) : (
            <>
              <TableScroller>
                <table className="w-full min-w-3xl border-collapse">
                  <thead>
                    <tr className="border-b border-outline-variant">
                      <Th>Tanggal</Th>
                      <Th>Pengguna</Th>
                      <Th>Invoice</Th>
                      <Th className="text-right">Kredit</Th>
                      <Th className="text-right">Total</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.data.map((topup) => (
                      <TopupRow key={topup.id} topup={topup} />
                    ))}
                  </tbody>
                </table>
              </TableScroller>

              <Pagination
                page={data.pagination.page}
                totalPages={data.pagination.totalPages}
                total={data.pagination.total}
                onPageChange={setPage}
              />
            </>
          )}
        </CardBody>
      </Card>
    </>
  )
}

function TopupRow({ topup }: { topup: AdminTopup }) {
  const meta = STATUS_META[topup.status]

  // Money in without a credit out is the failure that costs real money, so the
  // row says so rather than looking like any other paid invoice.
  const uncredited = topup.status === 'paid' && !topup.creditedAt
  const mismatched =
    topup.paidAmountIdr !== null && topup.paidAmountIdr !== topup.totalIdr

  return (
    <tr className="border-b border-outline-variant/50 transition-colors hover:bg-surface-container-low">
      <Td className="whitespace-nowrap text-label-md">{formatDateTime(topup.createdAt)}</Td>
      <Td>
        <span className="block font-medium text-on-surface">{topup.userName}</span>
        <span className="block text-label-sm text-on-surface-variant">{topup.userEmail}</span>
      </Td>
      <Td>
        <span className="block font-mono text-label-sm text-on-surface-variant">{topup.id}</span>
        {topup.providerReference ? (
          <span className="block font-mono text-label-sm text-on-surface-variant">
            {topup.providerReference}
          </span>
        ) : null}
        <span className="mt-1.5 block">
          <Badge tone={meta.tone}>{meta.label}</Badge>
        </span>
        {uncredited ? (
          <span className="mt-1 block text-label-sm text-error">Saldo belum ditambahkan</span>
        ) : null}
      </Td>
      <Td className="text-right tabular-nums">{formatUsd(topup.usdAmount)}</Td>
      <Td className="text-right tabular-nums">
        {formatIdr(topup.totalIdr)}
        {mismatched ? (
          <span className="block text-label-sm text-error">
            dibayar {formatIdr(topup.paidAmountIdr ?? 0)}
          </span>
        ) : null}
      </Td>
    </tr>
  )
}
